// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

/**
 * The ID of the RTK Query tracker.
 *
 * @constant
 */
export const RTK_PROMISE_TRACKER_ID = 'rtk-query';

/**
 * Shared tag types enum.
 *
 * Centralized enum for all shared cache tag types used across multiple RTK Query
 * APIs. This ensures type safety and makes it easy to refactor tag names across the
 * entire codebase.
 *
 * @remarks
 * These tags are shared across different APIs (brokerage, AI, engine) and are used
 * for cache invalidation when shared state changes (e.g., currency changes).
 * @example
 *
 * ```typescript
 * providesTags: result =>
 *   result
 *     ? [{ type: 'Portfolio', id: '123' }, SharedTagTypes.CurrencyConversion]
 *     : [];
 * ```
 */
export enum SharedTagTypes {
  /**
   * Tag for queries that include currency conversion support.
   *
   * This tag marks queries whose results contain monetary values that have been
   * converted to the user's selected account currency. When the user changes their
   * currency preference, queries with this tag are invalidated to trigger
   * recalculation of currency conversions without refetching the raw data.
   */
  CurrencyConversion = 'CurrencyConversion',
}

/**
 * The shared tag types array.
 *
 * This array is used to register shared tag types in RTK Query API configurations.
 * It's derived from the {@link SharedTagTypes} enum to ensure consistency.
 *
 * @constant
 */
export const SHARED_TAG_TYPES = [SharedTagTypes.CurrencyConversion] as const;

/**
 * The symbol for the RTK Query track option.
 *
 * @constant
 */
export const RTK_QUERY_TRACK_OPTION = Symbol('@rtk-query/track');

/**
 * The option to disable the tracking of the RTK Query.
 *
 * @constant
 */
export const RTK_QUERY_DISABLE_TRACK_OPTION = {
  [RTK_QUERY_TRACK_OPTION]: false,
} as const;

/** SessionStorage key for PKCE code_verifier (used by getLoginUrl). */
export const PKCE_VERIFIER_KEY = 'auth0_pkce_code_verifier';
